import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Zap, Edit, Trash2, Power } from 'lucide-react';
import { toast } from 'sonner';

const TRIGGERS = [
  { value: 'match_start', label: 'Maç Başlangıcı', icon: '🔔' },
  { value: 'goal_scored', label: 'Gol', icon: '⚽' },
  { value: 'halftime', label: 'Devre Arası', icon: '⏸️' },
  { value: 'fulltime', label: 'Maç Sonu', icon: '🏁' },
];
const CHANNELS = [
  { id: 'telegram', label: 'Telegram', color: '#0088CC' },
  { id: 'twitter', label: 'X (Twitter)', color: '#1DA1F2' },
  { id: 'instagram', label: 'Instagram', color: '#E4405F' },
];

export default function FootballAutomationPage() {
  const [rules, setRules] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', trigger_type: 'match_start', platforms: ['telegram'], custom_template: '', is_active: true });

  const load = () => api.get('/sports/auto-rules').then(r => setRules(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const togglePlatform = (p) => setForm(prev => ({ ...prev, platforms: prev.platforms.includes(p) ? prev.platforms.filter(c => c !== p) : [...prev.platforms, p] }));

  const handleSave = async () => {
    if (!form.name) { toast.error('Kural adı gerekli'); return; }
    try {
      if (editId) await api.put(`/sports/auto-rules/${editId}`, form);
      else await api.post('/sports/auto-rules', form);
      toast.success(editId ? 'Güncellendi' : 'Oluşturuldu');
      setShowDialog(false); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/sports/auto-rules/${id}`); toast.success('Silindi'); load(); } catch { toast.error('Hata'); }
  };

  const triggerInfo = (t) => TRIGGERS.find(tr => tr.value === t) || { label: t, icon: '📋' };

  return (
    <div data-testid="football-automation-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Otomatik Mesajlar</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Maç olaylarına göre otomatik içerik oluşturma kuralları</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm({ name: '', trigger_type: 'match_start', platforms: ['telegram'], custom_template: '', is_active: true }); setShowDialog(true); }} className="bg-white text-black hover:bg-[#E5E5E5] h-9 text-sm" data-testid="add-rule-btn"><Plus size={14} className="mr-1" /> Kural Ekle</Button>
      </div>

      <div className="space-y-3">
        {rules.length === 0 && <div className="text-center py-12"><Zap size={32} className="mx-auto text-[#262626] mb-3" /><p className="text-[#737373] font-ibm text-sm">Henüz otomasyon kuralı yok</p></div>}
        {rules.map(r => {
          const ti = triggerInfo(r.trigger_type || r.trigger);
          return (
            <Card key={r.id} className="bg-[#141414] border-[#262626]" data-testid={`rule-${r.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${r.is_active ? 'bg-[#FACC15]/10' : 'bg-[#262626]'}`}>{ti.icon}</div>
                    <div>
                      <h3 className="text-white font-ibm font-medium">{r.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">{ti.label}</Badge>
                        {(r.platforms || r.channels || []).map(ch => {
                          const ci = CHANNELS.find(c => c.id === ch);
                          return <Badge key={ch} variant="outline" className="text-xs capitalize" style={{ borderColor: (ci?.color || '#525252') + '40', color: ci?.color || '#A3A3A3' }}>{ci?.label || ch}</Badge>;
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={r.is_active ? 'bg-green-500/10 text-green-400 text-xs' : 'bg-red-500/10 text-red-400 text-xs'}>{r.is_active ? 'Aktif' : 'Pasif'}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => { setEditId(r.id); setForm({ name: r.name, trigger_type: r.trigger_type || r.trigger, platforms: r.platforms || r.channels || [], custom_template: r.custom_template || r.message_template || '', is_active: r.is_active }); setShowDialog(true); }} className="h-7 w-7 p-0 text-[#525252] hover:text-white"><Edit size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="h-7 w-7 p-0 text-[#525252] hover:text-red-400"><Trash2 size={14} /></Button>
                  </div>
                </div>
                {(r.custom_template || r.message_template) && <pre className="mt-3 text-xs text-[#737373] font-ibm bg-[#0A0A0A] p-3 rounded border border-[#1A1A1A] whitespace-pre-wrap">{r.custom_template || r.message_template}</pre>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit text-white">{editId ? 'Kural Düzenle' : 'Yeni Kural'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Kural Adı</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="rule-name-input" /></div>
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Tetikleyici</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">{TRIGGERS.map(t => (
                <button key={t.value} onClick={() => setForm({...form, trigger_type: t.value})} className={`p-3 rounded-md border text-left text-sm font-ibm ${form.trigger_type === t.value ? 'border-[#FACC15] bg-[#FACC15]/10 text-white' : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3]'}`}>{t.icon} {t.label}</button>
              ))}</div></div>
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Kanallar</Label>
              <div className="flex gap-2 mt-1">{CHANNELS.map(ch => (
                <button key={ch.id} onClick={() => togglePlatform(ch.id)} className={`px-4 py-2 rounded text-xs font-ibm ${form.platforms.includes(ch.id) ? 'text-white' : 'text-[#737373] border border-[#262626]'}`} style={form.platforms.includes(ch.id) ? { background: ch.color + '20', borderColor: ch.color + '40', border: `1px solid ${ch.color}40` } : {}}>{ch.label}</button>
              ))}</div></div>
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Özel Şablon (opsiyonel)</Label>
              <Textarea value={form.custom_template} onChange={e => setForm({...form, custom_template: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[80px] font-jetbrains text-sm" placeholder="Boş bırakılırsa varsayılan şablon kullanılır..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#262626] text-[#A3A3A3]">İptal</Button>
            <Button onClick={handleSave} className="bg-white text-black hover:bg-[#E5E5E5]">{editId ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
